// Tax brackets tab: income brackets by jurisdiction, with create/remove.
// Reads and writes only the confirmed brackets endpoints.
//
// Rates render as both a percent and basis points. Brackets carry an
// is_active flag in the response, so the state is shown explicitly — but
// there is no bracket update endpoint, so no activate/deactivate action is
// invented here. Removal is confirmed through the shared Dialog.
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { EmptyState } from "@sylviq/ui-react-web/components/ui/empty-state";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { StatusDot } from "@sylviq/ui-react-web/components/ui/status-dot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { CardField } from "@/components/RecordCard";
import {
  createTaxBracket,
  deleteTaxBracket,
  listTaxBrackets,
  type TaxBracket,
} from "@/lib/api";
import { humanizeLabel } from "@/lib/labels";

const DISPLAY_CURRENCY = "USD";

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function money(cents: number): string {
  return formatMoney({ cents, currency: DISPLAY_CURRENCY });
}

function rateWithBp(rate_bp: number): string {
  return `${(rate_bp / 100).toFixed(2)}% (${rate_bp} bp)`;
}

function toMinor(amount: string): number | null {
  const trimmed = amount.trim();
  if (!trimmed) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0) return null;
  const minor = Math.round(value * 100);
  return Number.isSafeInteger(minor) ? minor : null;
}

function toRateBp(percent: string): number | null {
  const trimmed = percent.trim();
  if (!trimmed) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0 || value > 100) return null;
  return Math.round(value * 100);
}

function bandLabel(bracket: TaxBracket): string {
  return [
    bracket.jurisdiction,
    bracket.filing_status,
    bracket.tax_year ? String(bracket.tax_year) : "",
  ]
    .filter(Boolean)
    .join(" · ");
}

const LEVELS = ["federal", "state", "local"] as const;

export function BracketsTab() {
  const [brackets, setBrackets] = useState<TaxBracket[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<TaxBracket | null>(null);

  const [name, setName] = useState("");
  const [level, setLevel] = useState("federal");
  const [jurisdiction, setJurisdiction] = useState("");
  const [filingStatus, setFilingStatus] = useState("");
  const [taxYear, setTaxYear] = useState(String(new Date().getFullYear()));
  const [minIncome, setMinIncome] = useState("");
  const [maxIncome, setMaxIncome] = useState("");
  const [percent, setPercent] = useState("");

  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    listTaxBrackets()
      .then(setBrackets)
      .catch((cause: unknown) => setError(toErrorMessage(cause)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(reload, [reload]);

  const submitCreate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const min_income_cents = toMinor(minIncome);
    const rate_bp = toRateBp(percent);
    if (!name.trim()) {
      setError("Give the bracket a name first.");
      return;
    }
    if (min_income_cents === null) {
      setError("Enter where this bracket starts (zero or more).");
      return;
    }
    if (rate_bp === null) {
      setError("Rate must be a percent from 0 to 100.");
      return;
    }
    const max_income_cents = maxIncome.trim() ? toMinor(maxIncome) : null;
    if (maxIncome.trim() && max_income_cents === null) {
      setError(
        "Bracket end must be an amount of zero or more, or left blank for no top.",
      );
      return;
    }
    if (max_income_cents !== null && max_income_cents < min_income_cents) {
      setError("Bracket end must be on or above where it starts.");
      return;
    }
    const parsedYear = taxYear.trim() ? Number(taxYear) : null;
    if (
      taxYear.trim() &&
      (!Number.isInteger(parsedYear) ||
        (parsedYear as number) < 2000 ||
        (parsedYear as number) > 2100)
    ) {
      setError("Enter a tax year like 2026, or leave it blank.");
      return;
    }
    setCreating(true);
    setError(null);
    setNotice(null);
    try {
      await createTaxBracket({
        name: name.trim(),
        jurisdiction_level: level,
        ...(jurisdiction.trim() ? { jurisdiction: jurisdiction.trim() } : {}),
        ...(filingStatus.trim() ? { filing_status: filingStatus.trim() } : {}),
        ...(parsedYear ? { tax_year: parsedYear } : {}),
        min_income_cents,
        ...(max_income_cents !== null ? { max_income_cents } : {}),
        rate_bp,
      });
      setNotice(`Added “${name.trim()}”.`);
      setName("");
      setJurisdiction("");
      setFilingStatus("");
      setMinIncome("");
      setMaxIncome("");
      setPercent("");
      reload();
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setCreating(false);
    }
  };

  const remove = async (bracket: TaxBracket) => {
    setError(null);
    setNotice(null);
    try {
      await deleteTaxBracket(bracket.id);
      setNotice(`Removed “${bracket.name}”.`);
      reload();
    } catch (cause) {
      setError(toErrorMessage(cause));
    }
  };

  return (
    <div className="grid gap-4">
      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          <div className="mt-3">
            <Button type="button" variant="secondary" onClick={reload}>
              Retry
            </Button>
          </div>
        </div>
      ) : null}
      {notice ? (
        <output className="surface block rounded-xl border border-[var(--status-success-foreground)] p-4 text-sm">
          {notice}
        </output>
      ) : null}

      <form
        onSubmit={submitCreate}
        className="surface grid gap-3 rounded-2xl p-5"
      >
        <h3 className="text-sm font-semibold">Add a bracket</h3>
        <div className="flex flex-wrap items-end gap-3">
          <label htmlFor="tb-name" className="grid gap-1 text-xs font-medium">
            Name
            <Input
              id="tb-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Federal single 22%"
              disabled={creating}
            />
          </label>
          <label htmlFor="tb-level" className="grid gap-1 text-xs font-medium">
            Level
            <select
              id="tb-level"
              className="select"
              value={level}
              onChange={(e) => setLevel(e.target.value)}
              disabled={creating}
            >
              {LEVELS.map((value) => (
                <option key={value} value={value}>
                  {humanizeLabel(value)}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor="tb-jur" className="grid gap-1 text-xs font-medium">
            Place (optional)
            <Input
              id="tb-jur"
              value={jurisdiction}
              onChange={(e) => setJurisdiction(e.target.value)}
              placeholder="US-CA"
              disabled={creating}
            />
          </label>
          <label htmlFor="tb-status" className="grid gap-1 text-xs font-medium">
            Filing status (optional)
            <Input
              id="tb-status"
              value={filingStatus}
              onChange={(e) => setFilingStatus(e.target.value)}
              placeholder="single"
              disabled={creating}
            />
          </label>
          <label htmlFor="tb-year" className="grid gap-1 text-xs font-medium">
            Tax year (optional)
            <Input
              id="tb-year"
              type="number"
              value={taxYear}
              onChange={(e) => setTaxYear(e.target.value)}
              disabled={creating}
            />
          </label>
          <label htmlFor="tb-min" className="grid gap-1 text-xs font-medium">
            Starts at ({DISPLAY_CURRENCY})
            <Input
              id="tb-min"
              type="number"
              min="0"
              step="0.01"
              value={minIncome}
              onChange={(e) => setMinIncome(e.target.value)}
              placeholder="0.00"
              disabled={creating}
            />
          </label>
          <label htmlFor="tb-max" className="grid gap-1 text-xs font-medium">
            Ends at ({DISPLAY_CURRENCY}, blank = no top)
            <Input
              id="tb-max"
              type="number"
              min="0"
              step="0.01"
              value={maxIncome}
              onChange={(e) => setMaxIncome(e.target.value)}
              placeholder="47150.00"
              disabled={creating}
            />
          </label>
          <label htmlFor="tb-rate" className="grid gap-1 text-xs font-medium">
            Rate (%)
            <Input
              id="tb-rate"
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={percent}
              onChange={(e) => setPercent(e.target.value)}
              placeholder="22"
              disabled={creating}
            />
          </label>
          <Button type="submit" disabled={creating}>
            {creating ? "Adding…" : "Add bracket"}
          </Button>
        </div>
      </form>

      {loading && brackets === null && !error ? (
        <div className="surface rounded-2xl p-5 text-sm text-[var(--muted-foreground)]">
          Loading…
        </div>
      ) : !error && brackets !== null && brackets.length === 0 ? (
        <EmptyState
          title="No brackets yet"
          reason="Add the income bands above — each one taxes the slice of income between where it starts and where it ends."
        />
      ) : brackets !== null && brackets.length > 0 ? (
        <>
          <div
            className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block"
            data-testid="tax-brackets-table"
          >
            <div className="table-scroll">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Bracket</TableHead>
                    <TableHead>Level</TableHead>
                    <TableHead className="text-right">Starts at</TableHead>
                    <TableHead className="text-right">Ends at</TableHead>
                    <TableHead className="text-right">Rate</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {brackets.map((bracket) => (
                    <TableRow key={bracket.id}>
                      <TableCell className="font-medium">
                        {bracket.name}
                        <span className="block text-xs font-normal text-[var(--muted-foreground)]">
                          {bandLabel(bracket) || "All places"}
                        </span>
                      </TableCell>
                      <TableCell>
                        {humanizeLabel(bracket.jurisdiction_level)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {money(bracket.min_income_cents)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {bracket.max_income_cents === undefined ||
                        bracket.max_income_cents === null
                          ? "No top"
                          : money(bracket.max_income_cents)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {rateWithBp(bracket.rate_bp)}
                      </TableCell>
                      <TableCell>
                        <StatusDot
                          tone={bracket.is_active ? "success" : "idle"}
                          label={bracket.is_active ? "Active" : "Inactive"}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() => setConfirmRemove(bracket)}
                        >
                          Remove
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          <div
            className="stack-list grid gap-3 min-[64rem]:hidden"
            data-testid="tax-brackets-stack"
          >
            {brackets.map((bracket) => (
              <article
                key={bracket.id}
                className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
              >
                <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                  <span className="record-title font-medium">
                    {bracket.name}
                  </span>
                  <StatusDot
                    tone={bracket.is_active ? "success" : "idle"}
                    label={bracket.is_active ? "Active" : "Inactive"}
                  />
                </div>
                <CardField label="Applies to">
                  {bandLabel(bracket) || "All places"}
                </CardField>
                <CardField label="Level">
                  {humanizeLabel(bracket.jurisdiction_level)}
                </CardField>
                <CardField label="Starts at">
                  <span className="tabular-nums">
                    {money(bracket.min_income_cents)}
                  </span>
                </CardField>
                <CardField label="Ends at">
                  <span className="tabular-nums">
                    {bracket.max_income_cents === undefined ||
                    bracket.max_income_cents === null
                      ? "No top"
                      : money(bracket.max_income_cents)}
                  </span>
                </CardField>
                <CardField label="Rate">
                  <span className="tabular-nums">
                    {rateWithBp(bracket.rate_bp)}
                  </span>
                </CardField>
                <div className="flex flex-wrap gap-2 pt-3">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setConfirmRemove(bracket)}
                  >
                    Remove
                  </Button>
                </div>
              </article>
            ))}
          </div>
        </>
      ) : null}

      <Dialog
        open={confirmRemove !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmRemove(null);
        }}
        title={confirmRemove ? `Remove “${confirmRemove.name}”?` : "Remove bracket?"}
        description={
          confirmRemove
            ? `Deletes the ${humanizeLabel(confirmRemove.jurisdiction_level)} bracket starting at ${money(confirmRemove.min_income_cents)}. Posted entries are not recalculated.`
            : undefined
        }
      >
        <div className="actions-row">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setConfirmRemove(null)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => {
              const bracket = confirmRemove;
              setConfirmRemove(null);
              if (bracket) void remove(bracket);
            }}
          >
            Remove bracket
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
